/**
 * CreatePortfolioModal — Plus only.
 *
 * Round 92o++++++++N++++++++++ (May 2026, simplified Jun 2026).
 *
 * Two-step creation flow:
 *
 *   Step 1 — FORM: Name only (required, red-highlight when empty).
 *            Broker + Account-type questions were removed because
 *            they are collected (and overridden per-lot) in the CSV
 *            importer that lands in Step 2. Asking twice with
 *            different defaults was overwriting the user's first
 *            choice and just doubling friction.
 *
 *   Step 2 — HOW: User chooses how to populate the new portfolio —
 *            Upload CSV, Add manually later, or (future) connect
 *            broker via Plaid. We create the portfolio_group first,
 *            set it as the active selection, then either launch the
 *            CSV importer with `portfolio_id` plumbed in (Phase 3
 *            backend already accepts this), or simply close so the
 *            user adds positions one by one.
 */

import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Upload, PlusCircle, Link2, AlertCircle } from 'lucide-react';
import { createPortfolio, setActive, deletePortfolio } from '@/lib/portfolioStore';
import CsvImportModal from './CsvImportModal';

export default function CreatePortfolioModal({ open, onClose }) {
  const [step, setStep] = useState('form');           // 'form' | 'how'
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createdPortfolio, setCreatedPortfolio] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);

  const nameInvalid = !name.trim();

  const reset = () => {
    setStep('form');
    setName('');
    setNameTouched(false);
    setSaving(false);
    setCreatedPortfolio(null);
  };

  // Round P108q (Feb 2026) — HARD GUARANTEE. The "+ New Portfolio"
  // button MUST always land on the naming step. Previously the modal
  // could reopen at step='how' when the last close happened while
  // csvOpen=true (P108n keeps createdPortfolio alive on purpose to
  // preserve the CSV importer's portfolio_id prop), or when Radix
  // dismissed the outer Dialog via backdrop / ESC / focus race
  // without letting our reset() run. That left the next click looking
  // like a "populate the existing test3" flow — a footgun with real
  // data-loss consequences: the .xlsx confirm dialog would show the
  // pre-existing portfolio's magnitude box and, on OK, wipe it.
  // Forcing a full reset on every open transition (false → true)
  // makes this state impossible to reach.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      reset();
    }
    wasOpenRef.current = open;
  }, [open]);

  const closeAll = () => {
    // Round P108n (Feb 2026) — CRITICAL scope-loss fix. If the nested
    // CsvImportModal is currently open (`csvOpen=true`), do NOT run
    // reset() — that call would `setCreatedPortfolio(null)`, which
    // in turn makes the nested modal's `portfolioId` prop go null
    // mid-flight. The next .xlsx confirm would then POST to
    // /portfolio/reset-all with an empty body → the (now-blocked)
    // global user-wide wipe path. The Radix outer Dialog can
    // dismiss itself via backdrop click, ESC, or a focus race when
    // the OS file picker opens; any of those without this guard
    // silently orphaned the portfolio_id state. Instead, just call
    // onClose (parent hook is safe to run — it only toggles the
    // opener's boolean). The nested modal's own onOpenChange handler
    // will call closeAll again when the user is truly done, and by
    // then csvOpen is false so reset() is safe.
    if (csvOpen) {
      onClose?.();
      return;
    }
    reset();
    onClose?.();
  };

  // Step 1 → Step 2. Creates the portfolio_group now (so the active
  // selection switches immediately) and advances the modal to the
  // "how to populate" step. Broker / account_type are intentionally
  // omitted from the payload — the backend defaults to 'other' and
  // the CSV importer (or per-lot edits later) sets the real values.
  const onAdvanceToHow = async (e) => {
    e?.preventDefault?.();
    if (nameInvalid) {
      setNameTouched(true);
      toast.error('Portfolio name is required.');
      return;
    }
    setSaving(true);
    try {
      const pf = await createPortfolio({ name: name.trim() });
      if (pf?.portfolio_id) {
        await setActive(pf.portfolio_id);
        setCreatedPortfolio(pf);
        setStep('how');
        // Notify dashboard / charts to re-fetch with the new active
        // portfolio. PortfolioSelector also fires this on switches.
        try { window.dispatchEvent(new Event('portfolio-changed')); } catch { /* SSR */ }
      } else {
        toast.error('Portfolio creation returned an empty payload.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create portfolio.');
    } finally {
      setSaving(false);
    }
  };

  const onLaunchCsv = () => {
    setCsvOpen(true);
  };

  const onFinishManual = () => {
    toast.success(`Portfolio "${createdPortfolio?.name || ''}" created. Add positions any time from the Add Position button.`);
    closeAll();
  };

  return (
    <>
      {/* Round P118 (Feb 2026) — Overlap fix. When the nested
          CsvImportModal opens (csvOpen=true), we hide the parent
          Radix Dialog so only ONE modal is visible at a time. The
          old behaviour rendered both on top of each other — the
          Radix Dialog at z-[200] sat on top of the custom CSV
          importer at z-[70], and the two backdrops interfered with
          pointer events on the "Upload CSV / Add manually" choice
          buttons (user reported: buttons unclickable, only the X
          worked). Keeping `open={open && !csvOpen}` mounts the
          Radix Dialog invisibly during CSV mode so its React state
          (createdPortfolio, step) survives — same intent as the
          existing P108n/P108q guards that kept it "alive" for the
          portfolio_id prop plumbing. */}
      <Dialog open={open && !csvOpen} onOpenChange={(v) => {
        // Ignore the programmatic close that fires when we hide
        // for CSV — only run closeAll for real user-initiated
        // dismissals (backdrop/ESC/X on the Radix content).
        if (!v && !csvOpen) closeAll();
      }}>
        <DialogContent
          className="max-w-md"
          data-testid="create-portfolio-modal"
        >
          <DialogHeader>
            <DialogTitle>
              {step === 'form' ? 'New Portfolio' : 'How do you want to add positions?'}
            </DialogTitle>
            <DialogDescription>
              {step === 'form'
                ? 'Name your portfolio (e.g. "Schwab Roth"). Broker + account type are set when you import positions. Plus tier: up to 5 portfolios, 500 tickers each.'
                : `"${createdPortfolio?.name || ''}" is ready. Pick how to populate it — you can change this any time later.`}
            </DialogDescription>
          </DialogHeader>

          {step === 'form' && (
            <form onSubmit={onAdvanceToHow} className="space-y-3">
              <div>
                <label htmlFor="pf-name" className="block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">
                  Portfolio name <span className="text-rose-500 normal-case">(required)</span>
                </label>
                <input
                  id="pf-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, 60))}
                  onBlur={() => setNameTouched(true)}
                  placeholder="e.g. Schwab Roth"
                  autoFocus
                  aria-invalid={nameInvalid}
                  className={`w-full rounded-md border-2 bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/40 transition-colors ${
                    nameInvalid
                      ? 'border-rose-400 dark:border-rose-600 bg-rose-50/40 dark:bg-rose-950/20'
                      : 'border-border'
                  }`}
                  data-testid="create-portfolio-name"
                />
                {nameInvalid && (
                  <p
                    className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-rose-600 dark:text-rose-400"
                    data-testid="create-portfolio-name-error"
                  >
                    <AlertCircle className="w-3 h-3" />
                    Give this portfolio a name (it shows up in the dropdown above the My Portfolio card).
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeAll}
                  disabled={saving}
                  className="flex-1 px-3 py-2 rounded-md text-sm font-semibold bg-muted hover:bg-muted/70 disabled:opacity-60"
                  data-testid="create-portfolio-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-3 py-2 rounded-md text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60"
                  data-testid="create-portfolio-submit"
                >
                  {saving ? 'Creating…' : 'Create portfolio'}
                </button>
              </div>
            </form>
          )}

          {step === 'how' && (
            <div className="space-y-2.5">
              <HowOptionRow
                icon={Upload}
                color="emerald"
                title="Upload CSV"
                description="Import positions from your broker's CSV export (Robinhood account-activity files auto-detected)."
                onClick={onLaunchCsv}
                testId="create-portfolio-how-csv"
              />
              <HowOptionRow
                icon={PlusCircle}
                color="violet"
                title="Add manually"
                description="I'll add positions one at a time from the Add Position button on the dashboard."
                onClick={onFinishManual}
                testId="create-portfolio-how-manual"
              />
              <HowOptionRow
                icon={Link2}
                color="sky"
                title="Connect my broker"
                description="Sync positions directly via Plaid."
                badge="Coming soon"
                disabled
                testId="create-portfolio-how-plaid"
              />
              <div className="pt-2 flex">
                <button
                  type="button"
                  onClick={closeAll}
                  className="ml-auto text-xs font-semibold text-muted-foreground hover:text-foreground"
                  data-testid="create-portfolio-how-close"
                >
                  I&rsquo;ll decide later
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Round 92o++++++++N++++++++++ — Phase 3 first cut. CSV
          importer is launched with the freshly-created portfolio_id
          so every imported lot lands inside the right group. On
          close, finalise the whole flow. */}
      <CsvImportModal
        open={csvOpen}
        onOpenChange={(v) => {
          if (!v) {
            setCsvOpen(false);
            // Close the parent Create modal too — the user is done.
            closeAll();
          }
        }}
        portfolioId={createdPortfolio?.portfolio_id || null}
        portfolioName={createdPortfolio?.name || ''}
        /* Round 92o++++++++N++++++++++ (Jun 2026) — orphan rollback.
           If the user closes the CSV importer without a successful
           import (Cancel / X / backdrop), we DELETE the empty
           portfolio we auto-created in Step 1 so the dropdown
           doesn't accumulate zombie "Test2 — 0 / 500 tickers"
           rows. Step 2's manual / Plaid paths still close the
           parent without triggering rollback because those flows
           keep the portfolio intentionally empty for now. */
        onClosedWithoutImport={async () => {
          const pid = createdPortfolio?.portfolio_id;
          if (!pid) return;
          try {
            await deletePortfolio(pid);
            toast.info(`"${createdPortfolio?.name || 'New portfolio'}" was cleaned up because no positions were imported.`);
            try { window.dispatchEvent(new Event('portfolio-changed')); } catch { /* SSR */ }
          } catch {
            // Best-effort — if the delete fails the user can clean
            // up manually from the dropdown's trash icon.
          }
        }}
      />
    </>
  );
}

function HowOptionRow({ icon: Icon, color, title, description, onClick, disabled, badge, testId }) {
  const colorClasses = {
    emerald: 'border-emerald-300 dark:border-emerald-700 hover:bg-emerald-50/60 dark:hover:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400',
    violet:  'border-violet-300 dark:border-violet-700 hover:bg-violet-50/60 dark:hover:bg-violet-950/30 text-violet-600 dark:text-violet-400',
    sky:     'border-sky-300 dark:border-sky-700 hover:bg-sky-50/60 dark:hover:bg-sky-950/30 text-sky-600 dark:text-sky-400',
  }[color] || '';
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testId}
      className={`w-full text-left rounded-lg border-2 px-3 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colorClasses}`}
    >
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-foreground">{title}</span>
            {badge && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                {badge}
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{description}</p>
        </div>
      </div>
    </button>
  );
}
